import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Checkbox,
	CheckboxOnChangeData,
	Divider,
	TableCell,
	TableCellLayout,
	TableRow,
	Text,
	mergeClasses,
} from "@fluentui/react-components";
import { Flex, GapSize } from "@/components/atoms/flex";
import {
	AgentMemberUpdatedDto,
	SupervisorAgentDto,
	SupervisorTableColumnNames,
} from "./types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	fetchSupervisorAgents,
	setQueueDesire,
	setQueueJoinStatus,
} from "@/repository/queryFns/supervisor";
import { formatDate } from "@/common/helpers/dateHelper";
import Timer from "@/components/atoms/timer/Timer";
import { SkillsDefaults } from "@/common/constants/skills";
import { useCommonStyle } from "@/common/style/commonStyle";
import { useTableStyle } from "@/components/atoms/customTable";
import { TableColumnInfo } from "@/common/types";
import {
	ArrowCounterclockwise16Regular,
	ArrowUndo16Regular,
	Checkmark20Regular,
	ChevronDown20Regular,
	LineFilled,
	SparkleFilled,
} from "@fluentui/react-icons";
import { useStyle } from "./style";
import { useIntl } from "react-intl";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import {
	AgentWorkloadNotification,
	InternalPresenceDto,
	PresenceAvailability,
	QueueType,
	SupervisedQueue,
	SupervisedQueues,
	SupervisorAgentsFilterData,
} from "@/repository/models";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState, CacheKey } from "@/common/stores/persistingStores/app";
import { Activity, Availabilities } from "../currentUserPresence";
import { PopoverView } from "@/components/atoms/popover";
import { SupervisorAgentJoinedTo } from "./SupervisorAgentJoinedTo";
import { Desire } from "@/components/molecules/desire/Desire";
import { CheckedState } from "@/components/molecules/multiCheckbox";
import { DesireType } from "@/components/molecules/desire";
import { UserPresence } from "../userPresence/UserPresence";
import { WorkloadMenu } from "@/components/molecules/workloadMenu";
import { WorkloadMenuType } from "@/components/molecules/workloadMenu/types";
import { agentWorkloadQueryKey } from "@/repository/queryFns";
import {
	SupervisorPanelHeader,
	SupervisorListMobile,
} from "@/components/organisms/supervisor";
import {
	useSupervisorAgentsFilterStore,
	SupervisorAgentsFilterState,
} from "@/common/stores/nonpersistingStores/supervisorAgentsFilter";
import { translateActivity } from "@/common/helpers/presenceHelper";
import { RefreshAgentButton } from "@/components/atoms/refreshAgent";
import { useTableColumns } from "@/hooks/tables";
import { CustomTable } from "@/components/atoms/customTable/CustomTable";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import {
	SupervisorQueuesState,
	useSupervisorQueuesStore,
} from "@/common/stores/nonpersistingStores/supervisor";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { TabHeader } from "@/components/atoms/tabHeader/TabHeader";
import { TabType } from "@/common/stores/persistingStores/navigation";
import { SignOutButton } from "@/components/atoms/signOutButton";
import { useAccountStyle } from "../account/style";
import { useDebounce } from "@/common/helpers/useDebounce";

export const SupervisorPanel = () => {
	const classes = useStyle();
	const buttonClasses = useAccountStyle();
	const tableClasses = useTableStyle();
	const commonStyle = useCommonStyle();
	const intl = useIntl();
	const queryClient = useQueryClient();

	const supervisorShowStatusTimer = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.supervisorShowStatusTimer
	);
	const supervisorAllowLogoutAgents = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.supervisorAllowLogoutAgents
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const allReachableQueuesIds = useMemo(() => new Set(queues.map((x) => x.id)), [queues]);

	const supervisorQueues = useSupervisorQueuesStore(
		(state: SupervisorQueuesState) => state.queues
	);

	const supervisorAgentsFilterState = useSupervisorAgentsFilterStore(
		(state: SupervisorAgentsFilterState) => state.filterData
	);
	
	const [debouncedSupervisorAgentsFilterState, setDebouncedSupervisorAgentsFilterState] = useState(supervisorAgentsFilterState);
	const [updateDebouncedSupervisorAgentsFilter] = useDebounce((value: SupervisorAgentsFilterData) => {
    	setDebouncedSupervisorAgentsFilterState(value);
	}, 300);

	useEffect(() => {
		updateDebouncedSupervisorAgentsFilter(supervisorAgentsFilterState)
	}, [supervisorAgentsFilterState]);

	const { isMobile } = useResponsiveBreakpoints();

	const isOnlyOneQueueSelectedOrAvailable =
		supervisorAgentsFilterState.queueIds.length === 1 ||
		supervisorQueues.length === 1;

	const prevFilterRef = useRef({
		queueIds: supervisorAgentsFilterState.queueIds,
		queuesLength: supervisorQueues.length,
	});

	const queryKey = ["supervisor", debouncedSupervisorAgentsFilterState];

	const fetchSupervisorAgentCallback = useCallback(
		() => {
			return fetchSupervisorAgents(debouncedSupervisorAgentsFilterState);
		},
		[debouncedSupervisorAgentsFilterState, queryKey]
	);

	const { data: supervisorTableItems } = useQuery<SupervisorAgentDto[]>({
		queryKey: queryKey,
		queryFn: () => fetchSupervisorAgentCallback(),
		refetchOnMount: "always",
		gcTime: 0,
		refetchInterval: 30000,
	});
	//#warning-ts [performance] should not refetch, except when receiving a changed notification

	const removeAgentById = useCallback(
		(agentId: string | undefined) => {
			if (!agentId) {
				return;
			}

			queryClient.setQueryData<SupervisorAgentDto[]>(queryKey, (oldData) =>
				oldData?.filter((item) => item.id !== agentId)
			);
		},
		[queryKey]
	);

	const isVisibleAfterPresenceUpdate = useCallback(
		(internalPresenceDto: InternalPresenceDto) => {
			if (
				!supervisorAgentsFilterState.presences ||
				supervisorAgentsFilterState.presences?.length === 0
			) {
				return true;
			}

			if (
				!supervisorAgentsFilterState.presences?.includes(
					internalPresenceDto.presence.availability
				)
			) {
				return false;
			}

			if (supervisorAgentsFilterState.hasClient === null) {
				return true;
			}

			return (
				supervisorAgentsFilterState.hasClient === internalPresenceDto.hasClient
			);
		},
		[supervisorAgentsFilterState]
	);

	const isVisibleAfterTeamsClientUpdate = useCallback(
		(userTeamsClientChangedDto: any) => {
			if (supervisorAgentsFilterState.hasClient === null) {
				return true;
			}

			return (
				supervisorAgentsFilterState.hasClient ===
				userTeamsClientChangedDto.hasClient
			);
		},
		[supervisorAgentsFilterState]
	);

	const isVisibleAfterAgentUpdate = useCallback(
		(queues: SupervisedQueue[]) => {
			if (
				supervisorAgentsFilterState.desire?.length === 0 &&
				supervisorAgentsFilterState.queueIds.length === 0 &&
				!supervisorAgentsFilterState.isJoined
			) {
				return true;
			}

			const desireValuesLength = supervisorAgentsFilterState.desire?.length;

			if (desireValuesLength && desireValuesLength !== 3) {
				const isVisible =
					queues.filter((x) =>
						supervisorAgentsFilterState.desire?.includes(x.desire)
					).length > 0;

				if (isVisible === false) {
					return false;
				}
			}

			if (supervisorAgentsFilterState.queueIds?.length > 0) {
				const isVisible =
					queues.filter((x) =>
						supervisorAgentsFilterState.queueIds?.includes(x.id)
					).length > 0;

				if (isVisible === false) {
					return false;
				}
			}

			if (supervisorAgentsFilterState.isJoined) {
				return (
					queues.filter(
						(x) => x.isJoined === supervisorAgentsFilterState.isJoined
					).length > 0
				);
			}

			return true;
		},
		[supervisorAgentsFilterState]
	);

	notificationListener(
		ServerEvent.SupervisorAgentsUpdated,
		(data: AgentMemberUpdatedDto) => {
			if (!supervisorTableItems) {
				return;
			}

			const supervisorItems = supervisorTableItems
				? [...supervisorTableItems]
				: [];

			data.updated.forEach((agent) => {
				const agentIndex = supervisorItems?.findIndex((x) => x.id === agent.Id);

				if (agentIndex === -1) {
					// agent not found, agent was membered, refresh to get queue data
					queryClient.invalidateQueries({ queryKey: queryKey });

					return;
				}

				const queueIndex = supervisorItems[agentIndex]?.queues?.findIndex(
					(x) => x.id === data.queueId
				);

				if (queueIndex === -1) {
					// queue not found, queue was joined, refresh to get queue data
					queryClient.invalidateQueries({ queryKey: queryKey });

					return;
				}

				let queues = [...supervisorItems![agentIndex]!.queues!];
				const queue: SupervisedQueue = { ...queues![queueIndex!]! };

				if (agent.Desire > 0) {
					queue.desire = agent.Desire;
				}

				queue.isJoined = agent.IsJoined;

				queues![queueIndex!] = queue;

				const isVisible = isVisibleAfterAgentUpdate(queues);

				if (isVisible) {
					supervisorItems[agentIndex] = {
						...supervisorItems[agentIndex]!,
						queues: queues,
					};
				} else {
					supervisorItems.splice(agentIndex!, 1);
				}
			});

			data.deleted.forEach((agentId) => {
				const agentIndex = supervisorItems?.findIndex((x) => x.id === agentId);
				if (agentIndex === -1) {
					return;
				}

				const queueIndex = supervisorItems[agentIndex]?.queues?.findIndex(
					(x) => x.id === data.queueId
				);

				if (queueIndex === -1) {
					return;
				}

				let queues = [...supervisorItems![agentIndex]!.queues!];

				queues.splice(queueIndex!, 1);

				if (queues.length == 0) {
					supervisorItems.splice(agentIndex!, 1);
				} else {
					const isVisible = isVisibleAfterAgentUpdate(queues);

					if (isVisible) {
						supervisorItems[agentIndex] = {
							...supervisorItems[agentIndex]!,
							queues: queues,
						};
					} else {
						supervisorItems.splice(agentIndex!, 1);
					}
				}
			});

			queryClient.setQueryData<SupervisorAgentDto[]>(queryKey, supervisorItems);
		}
	);

	notificationListener(
		ServerEvent.InternalPresenceChanged,
		(internalPresenceDto: InternalPresenceDto) => {
			if (!supervisorTableItems) {
				return;
			}

			const agentIndex = supervisorTableItems?.findIndex(
				(x) => x.id === internalPresenceDto.presence.id
			);

			const isVisible = isVisibleAfterPresenceUpdate(internalPresenceDto);

			if (
				agentIndex === -1 ||
				containsHasClient(
					supervisorTableItems[agentIndex]!,
					internalPresenceDto
				)
			) {
				if (isVisible) {
					queryClient.invalidateQueries({ queryKey: queryKey });
				}

				return;
			}

			const supervisorItems = supervisorTableItems
				? [...supervisorTableItems]
				: [];

			if (isVisible) {
				let reason = internalPresenceDto.reason ?? "";
				if (
					internalPresenceDto.presence.activity === Activity.AvailableLocked
				) {
					reason = Activity.AvailableLocked;
				}

				let agent = {
					...supervisorItems[agentIndex]!,
					presence: internalPresenceDto.presence,
					hasClient: internalPresenceDto.hasClient,
					reason: reason,
					lastHandled: internalPresenceDto.lastHandledTime,
				};
				supervisorItems[agentIndex] = agent;

				queryClient.setQueryData<SupervisorAgentDto[]>(
					queryKey,
					supervisorItems
				);
			} else {
				removeAgentById(supervisorItems[agentIndex]?.id);
			}
		}
	);

	notificationListener(
		ServerEvent.UserTeamsClientChanged,
		(userTeamsClientChangedDto: any) => {
			if (!supervisorTableItems) {
				return;
			}

			const agentIndex = supervisorTableItems?.findIndex(
				(x) => x.id === userTeamsClientChangedDto.userId
			);

			const isVisible = isVisibleAfterTeamsClientUpdate(
				userTeamsClientChangedDto
			);

			if (agentIndex === -1) {
				if (isVisible) {
					queryClient.invalidateQueries({ queryKey: queryKey });
				}

				return;
			}

			const supervisorItems = supervisorTableItems
				? [...supervisorTableItems]
				: [];

			if (isVisible) {
				let agent = {
					...supervisorItems[agentIndex]!,
					hasClient: userTeamsClientChangedDto.hasClient,
					presence: userTeamsClientChangedDto.presence,
				};

				supervisorItems[agentIndex] = agent;

				queryClient.setQueryData<SupervisorAgentDto[]>(
					queryKey,
					supervisorItems
				);
			} else {
				removeAgentById(supervisorItems[agentIndex]?.id);
			}
		}
	);

	notificationListener(
		ServerEvent.SupervisedAgentWorkloadChanged,
		(notification: AgentWorkloadNotification) => {
			if (!supervisorTableItems) {
				return;
			}
			const agentIndex = supervisorTableItems?.findIndex(
				(x) => x.id === notification.agentId
			);

			if (agentIndex === -1) {
				return;
			}

			const supervisorItems = supervisorTableItems
				? [...supervisorTableItems]
				: [];

			let agent = {
				...supervisorItems[agentIndex]!,
				workload: notification.totalWorkload,
			};

			supervisorItems[agentIndex] = agent;

			queryClient.setQueryData<SupervisorAgentDto[]>(queryKey, supervisorItems);

			queryClient.invalidateQueries({
				queryKey: [agentWorkloadQueryKey, notification.agentId],
			});
		}
	);

	const queuesJoinMutation = useMutation({
		mutationFn: (queues: SupervisedQueues) => setQueueJoinStatus(queues),
	});

	const updateJoinState = useCallback(
		(queues: SupervisedQueues) => {
			const supervisorItems = supervisorTableItems
				? [...supervisorTableItems]
				: [];

			const agentIndex = supervisorItems?.findIndex(
				(x) => x.id === queues.agentId
			);
			if (agentIndex === -1) {
				return;
			}

			let agentQueues = [...supervisorItems![agentIndex]!.queues!];

			queues.queueIds.forEach((id: string) => {
				const queueIndex = agentQueues.findIndex((x) => x.id === id);

				if (queueIndex === -1) {
					return;
				}

				const queue: SupervisedQueue = {
					...agentQueues![queueIndex!]!,
					isJoined: queues.isJoined,
				};
				agentQueues![queueIndex!] = queue;
			});

			supervisorItems[agentIndex] = {
				...supervisorItems[agentIndex]!,
				queues: agentQueues,
			};

			queryClient.setQueryData<SupervisorAgentDto[]>(queryKey, supervisorItems);
		},
		[supervisorTableItems]
	);

	const onJoinCheckBoxChange = useCallback(
		(queue: SupervisedQueue, checked: CheckedState) => {
			const queues: SupervisedQueues = {
				queueIds: [queue.id],
				agentId: queue.agentId,
				type: queue.type,
				isJoined: !!checked,
			};

			updateJoinState(queues);
			queuesJoinMutation.mutate(queues);
		},
		[updateJoinState]
	);

	const onJoinAllCheckBoxChange = useCallback(
		(agentId: string, checked: CheckedState) => {
			if (!supervisorTableItems) {
				return;
			}

			const agentIndex = supervisorTableItems?.findIndex(
				(x) => x.id === agentId
			);
			if (agentIndex === -1) {
				return;
			}

			const allQueues: SupervisedQueue[] = [
				...(supervisorTableItems![agentIndex!]!.queues || []),
			];

			const allQueuesIds = allQueues.map((x) => x.id);

			const idsToJoin = queues
				.filter((x) => x.isSupervisor && allQueuesIds.includes(x.id))
				.map((x) => x.id);

			updateJoinState({
				queueIds: idsToJoin,
				agentId: agentId,
				isJoined: !!checked,
			});

			mutateQueueByType(allQueues, agentId, QueueType.Voice, !!checked);
			mutateQueueByType(allQueues, agentId, QueueType.Messaging, !!checked);
		},
		[supervisorTableItems]
	);

	const mutateQueueByType = useCallback(
		(
			allQueues: SupervisedQueue[],
			agentId: string,
			type: QueueType,
			checked: boolean
		) => {
			const queuesIds = allQueues
				.filter((q) => q.type === type)
				.map((q) => q.id);

			if (queuesIds.length > 0) {
				const queues: SupervisedQueues = {
					queueIds: queuesIds,
					agentId: agentId,
					type: type,
					isJoined: !!checked,
				};
				queuesJoinMutation.mutate(queues);
			}
		},
		[supervisorTableItems]
	);

	const containsHasClient = (
		agent: SupervisorAgentDto,
		update: InternalPresenceDto
	): boolean => {
		if (!agent) {
			return false;
		}

		if (update.hasClient !== agent.hasClient) {
			return true;
		}

		const offlines = [PresenceAvailability.None, PresenceAvailability.Offline];
		const updatedInOffline = offlines.includes(
			update.presence.presenceType ?? PresenceAvailability.None
		);
		const agentInOffline = offlines.includes(
			agent.presence.presenceType ?? PresenceAvailability.None
		);

		return updatedInOffline != agentInOffline;
	};

	const getAgentCellContent = (agent: SupervisorAgentDto) => {
		const isJoinedOnAnyQueue = agent.queues
			? agent.queues.some((x) => x.isJoined)
			: false;

		return (
			<Flex gap={GapSize.Small} vAlign="center" hAlign="flex-start">
				<UserPresence
					queueIds={agent.queues?.map((q) => q.id)}
					avatarView={{
						agentState: {
							id: agent.id,
							fullName: agent.name,
							availability: agent.presence?.availability,
							activity: agent.presence?.activity,
						},
						size: 32,
						className: !isJoinedOnAnyQueue ? classes.isNotJoinedBorder : "",
						photoId: agent.isAiAgent ? undefined : agent.id,
					}}
					isDisabled={!agent.isSupervisorOnAtLeastOneQueue || agent.isAiAgent}
					allowActionsWhenDisabled={agent.isAiAgent && agent.isSupervisorOnAtLeastOneQueue}
					additionalActions={[
						<>
							{supervisorAllowLogoutAgents && !agent.isAiAgent && (
								<SignOutButton
									className={buttonClasses.button}
									userId={agent.id}
									agentName={agent.name}
									disabled={!agent.hasClient}
								/>
							)}
							<RefreshAgentButton agentToRefreshId={agent.id} />
						</>,
					]}
				/>
				<Text title={agent.name} className={tableClasses.ellipsis}>
					{agent.name}
					{agent.isAiAgent && <SparkleFilled />}
				</Text>
			</Flex>
		);
	};	

	const getHasClientStatusIcon = (hasClient: boolean) => {
		return hasClient ? (
			<Checkmark20Regular />
		) : (
			<LineFilled className={classes.doesntHaveClientStatusIcon} />
		);
	};

	const getJoinedToSingleQueueCellContent = useCallback(
		(agent: SupervisorAgentDto) => {
			const agentQueue =
				agent.queues && agent.queues.length > 0 ? agent.queues[0] : undefined;

			if (!agentQueue) {
				return null;
			}

			const queue = queues.find((q) => q.id === agentQueue.id);
			const isDisabled =
							queue?.hasAgentsActivityRight &&
							!queue.isSupervisor;

			return (
				<Checkbox
					checked={!!agentQueue.isJoined}
					key={agent.id}
					onChange={(_: any, data: CheckboxOnChangeData) => {
						onJoinCheckBoxChange(agentQueue, data.checked);
					}}
					disabled={isDisabled}
					label={agentQueue.name}
					className={classes.checkboxItem}
				/>
			);
		},
		[onJoinCheckBoxChange, queues]
	);

	const getDesireCellContent = (agent: SupervisorAgentDto) => {
		if (!agent.queues || agent.queues.length === 0) {
			return null;
		}

		const queue = agent.queues[0];
		if (!queue) {
			return null;
		}

		return <Desire data={queue} onChange={onDesireChange} />;
	};

	const onDesireChange = useCallback(
		(data: SupervisedQueue, desire: DesireType) => {
			queueDesireMutation.mutate({ ...data, desire: desire });
		},
		[supervisorTableItems]
	);

	const getJoinedToMultipleQueuesCellContent = useCallback(
		(agent: SupervisorAgentDto) => {
			const joinedQueuesNumber = agent.queues
				? agent.queues.filter((x) => x.isJoined && allReachableQueuesIds.has(x.id)).length
				: 0;

			const agentReachableQueues =  agent.queues?.filter((x) => allReachableQueuesIds.has(x.id)) || [];

			return (
				<Flex gap={GapSize.Small} vAlign="center">
					<PopoverView
						classes={{ content: classes.joinedToPopover }}
						positioning={{
							position: "below",
							align: "end",
							offset: () => ({ mainAxis: 10 }),
						}}
						trigger={
							<Flex
								vAlign="center"
								gap={GapSize.Large}
								className={classes.joinToPopoverTrigger}
							>
								<div>
									<b
										className={
											joinedQueuesNumber == 0 ? classes.isNotJoinedColor : ""
										}
									>
										{joinedQueuesNumber}
									</b>
									/
									{`${agentReachableQueues.length} ${intl.formatMessage({
										id: `Supervisor.JoinToQueues`,
									})}`}
								</div>
								<ChevronDown20Regular />
							</Flex>
						}
						content={
							<SupervisorAgentJoinedTo
								id={agent.id}
								queues={agent.queues!}
								onJoinChange={onJoinCheckBoxChange}
								onJoinAllChange={onJoinAllCheckBoxChange}
								onDesireChange={onDesireChange}
							/>
						}
					/>
				</Flex>
			);
		},
		[onJoinCheckBoxChange, onJoinAllCheckBoxChange, onDesireChange, allReachableQueuesIds]
	);

	const queueDesireMutation = useMutation({
		mutationFn: (data: SupervisedQueue) => setQueueDesire(data),
		onMutate: (queue: SupervisedQueue) => {
			const supervisorItems = supervisorTableItems
				? [...supervisorTableItems]
				: [];

			const agentIndex = supervisorItems?.findIndex(
				(x) => x.id === queue.agentId
			);

			if (agentIndex === -1) {
				queryClient.invalidateQueries({ queryKey });
				return;
			}

			const agent = supervisorItems[agentIndex];
			if (!agent || !agent.queues) {
				queryClient.invalidateQueries({ queryKey });
				return;
			}

			const queueIndex = agent.queues.findIndex(
				(x: SupervisedQueue) => x.id === queue.id
			);

			if (queueIndex == -1) {
				queryClient.invalidateQueries({ queryKey });

				return;
			}

			const updatedQueues = agent.queues.map((q, index) => {
				if (index === queueIndex) {
					return { ...q, desire: queue.desire };
				}

				return q;
			});

			supervisorItems[agentIndex] = {
				...agent,
				queues: updatedQueues,
			};

			queryClient.setQueryData<SupervisorAgentDto[]>(queryKey, supervisorItems);
		},
	});

	const agentTimer = useCallback(
		(item: SupervisorAgentDto) => {
			if (!item.presence) {
				return "";
			}

			return supervisorShowStatusTimer &&
				item.presence.availability !== Availabilities.Offline &&
				!!item.presence.updatedAt ? (
				<Timer startDate={item.presence.updatedAt} />
			) : (
				""
			);
		},
		[supervisorShowStatusTimer]
	);

	const workloadMenu = useCallback((item: SupervisorAgentDto) => {
		return (
			<WorkloadMenu
				agentId={item.id}
				menuType={WorkloadMenuType.SupervisedAgent}
				trigger={
					<span className={classes.agentWorkload}>{item.workload}%</span>
				}
				totalWorkload={item.workload}
				openOnHover={true}
			/>
		);
	}, []);

	const supervisedAgentsTableColumnInfo: TableColumnInfo[] = useMemo(
		() => [
			{
				columnId: SupervisorTableColumnNames.Agent,
				minWidth: 200,
				idealWidth: 250,
				notRemovable: true,
				renderCell: (item: SupervisorAgentDto) => (
					<TableCellLayout truncate>
						{getAgentCellContent(item)}
					</TableCellLayout>
				),
				fieldName: "name",
			},
			{
				columnId: SupervisorTableColumnNames.Skills,
				minWidth: 130,
				idealWidth: 180,
				renderCell: (
					item: SupervisorAgentDto //to be replaced with SkillTableCell
				) => (
					<TableCellLayout truncate>
						<Text
							className={classes.skillsText}
							title={item.skills?.map((skill) => skill.skillName).join(", ")}
							truncate
						>
							{item.skills?.map((skill, index) => (
								<span
									className={
										skill.skillLevel === SkillsDefaults.MaxSkillLevel
											? commonStyle.semiBold
											: ""
									}
								>
									{skill.skillName}
									{index < (item.skills?.length ?? 0) - 1 ? ", " : ""}
								</span>
							))}
						</Text>
					</TableCellLayout>
				),
				fieldName: "skills",
				compare: (itemA: SupervisorAgentDto, itemB: SupervisorAgentDto) => {
					const skillsA = itemA.skills?.flatMap((x) => x.skillName).join(",");
					const skillsB = itemB.skills?.flatMap((x) => x.skillName).join(",");

					return (skillsA ?? "").localeCompare(skillsB ?? "");
				},
			},
			{
				columnId: SupervisorTableColumnNames.LastHandled,
				minWidth: 180,
				idealWidth: 180,
				renderCell: (item: SupervisorAgentDto) => (
					<TableCellLayout>
						{item.lastHandled ? formatDate(item.lastHandled) : ""}
					</TableCellLayout>
				),
				fieldName: "lastHandled",
			},
			{
				columnId: SupervisorTableColumnNames.Workload,
				minWidth: 105,
				idealWidth: 105,
				renderCell: workloadMenu,
				fieldName: "workload",
				compare: (itemA: SupervisorAgentDto, itemB: SupervisorAgentDto) => {
					return (itemA.workload ?? 0) - (itemB.workload ?? 0);
				},
			},
			{
				columnId: SupervisorTableColumnNames.StatusTimer,
				minWidth: 120,
				idealWidth: 120,
				renderCell: (item: SupervisorAgentDto) => (
					<TableCellLayout truncate>{agentTimer(item)}</TableCellLayout>
				),
				fieldName: "statusTimer",
				compare: (itemA: SupervisorAgentDto, itemB: SupervisorAgentDto) => {
					const canRenderA =
						supervisorShowStatusTimer &&
						itemA.presence?.availability !== Availabilities.Offline &&
						!!itemA.presence?.updatedAt;
					const canRenderB =
						supervisorShowStatusTimer &&
						itemB.presence?.availability !== Availabilities.Offline &&
						!!itemB.presence?.updatedAt;

					if (!canRenderA && !canRenderB) {
						return 0;
					}
					if (!canRenderA) {
						return -1;
					}
					if (!canRenderB) {
						return 1;
					}

					const dateA = itemA.presence?.updatedAt
						? new Date(itemA.presence.updatedAt).getTime()
						: 0;
					const dateB = itemB.presence?.updatedAt
						? new Date(itemB.presence.updatedAt).getTime()
						: 0;

					return dateB - dateA;
				},
			},
			{
				columnId: SupervisorTableColumnNames.Reason,
				minWidth: 130,
				idealWidth: 180,
				renderCell: (item: SupervisorAgentDto) => (
					<TableCellLayout truncate title={item.reason}>
						{translateActivity(item.reason, intl)}
					</TableCellLayout>
				),
				fieldName: "reason",
			},
			{
				columnId: SupervisorTableColumnNames.HasClient,
				minWidth: 115,
				idealWidth: 115,
				renderCell: (item: SupervisorAgentDto) => (
					<TableCellLayout truncate>
						{getHasClientStatusIcon(item.hasClient)}
					</TableCellLayout>
				),
				fieldName: "hasClient",
				compare: (itemA: SupervisorAgentDto, itemB: SupervisorAgentDto) => {
					const a = itemA.hasClient ? 1 : 0;
					const b = itemB.hasClient ? 1 : 0;

					return a - b;
				},
			},
			{
				columnId: SupervisorTableColumnNames.JoinedTo,
				minWidth: 130,
				idealWidth: 180,
				renderCell: isOnlyOneQueueSelectedOrAvailable
					? getJoinedToSingleQueueCellContent
					: getJoinedToMultipleQueuesCellContent,
				compare: (itemA: SupervisorAgentDto, itemB: SupervisorAgentDto) => {
					const joinedA = itemA.queues?.filter((q) => q.isJoined).length ?? 0;
					const joinedB = itemB.queues?.filter((q) => q.isJoined).length ?? 0;

					return joinedA - joinedB;
				},
			},
			{
				columnId: SupervisorTableColumnNames.Desire,
				renderCell: getDesireCellContent,
				compare: (itemA: SupervisorAgentDto, itemB: SupervisorAgentDto) => {
					const desireA = itemA.queues?.[0]?.desire ?? 0;
					const desireB = itemB.queues?.[0]?.desire ?? 0;

					return desireA - desireB;
				},
				minWidth: 80,
				idealWidth: 80,
			},
		],
		[isOnlyOneQueueSelectedOrAvailable]
	);

	const {
		columns,
		removeColumn,
		addDeletedColumn,
		columnSizing,
		headerSortProps,
		rows,
		resetToDefaultTableSettings,
		resetToVisibleDefaultColumns,
		localTableSettingsCache,
	} = useTableColumns<SupervisorAgentDto>(
		supervisedAgentsTableColumnInfo,
		supervisorTableItems ?? [],
		CacheKey.SupervisedAgentsTableSettings,
		{ columnId: SupervisorTableColumnNames.Agent, direction: "ascending" }
	);

	useEffect(() => {
		const prev = prevFilterRef.current;
		const curr = {
			queueIds: supervisorAgentsFilterState.queueIds,
			queuesLength: supervisorQueues.length,
		};
		const queueIdsChanged =
			prev.queueIds.length !== curr.queueIds.length ||
			prev.queueIds.some((id, i) => id !== curr.queueIds[i]);
		const queuesLengthChanged = prev.queuesLength !== curr.queuesLength;

		if (queueIdsChanged || queuesLengthChanged) {
			resetToVisibleDefaultColumns();
			prevFilterRef.current = curr;
		}
	}, [
		supervisorAgentsFilterState.queueIds,
		supervisorQueues.length,
		resetToVisibleDefaultColumns,
	]);

	const getRowTemplate = useCallback(
		(item: SupervisorAgentDto) => {
			const hiddenColumnIds = localTableSettingsCache?.hiddenColumnIds ?? [];
			const isColumnVisible = (columnId: string) =>
				!hiddenColumnIds.includes(columnId.toString());
			// Workaround: Fixes a dependency issue where updates aren't triggering as expected.
			// Caused by complex dependency interactions.
			const visibleColumns = columns.filter(
				(c) =>
					c.columnId !== SupervisorTableColumnNames.JoinedTo &&
					c.columnId !== SupervisorTableColumnNames.Desire &&
					isColumnVisible(c.columnId.toString())
			);
			const renderJoinedToCell = isColumnVisible(
				SupervisorTableColumnNames.JoinedTo
			);
			const renderDesireCell =
				isColumnVisible(SupervisorTableColumnNames.Desire) &&
				isOnlyOneQueueSelectedOrAvailable;

			return (
				<TableRow key={item.id}>
					{visibleColumns.map((column) => (
						<TableCell key={`${column.columnId}-${item.id}`}>
							{column.renderCell(item)}
						</TableCell>
					))}

					{renderJoinedToCell && (
						<TableCell
							key={`${SupervisorTableColumnNames.JoinedTo}-${item.id}`}
						>
							{isOnlyOneQueueSelectedOrAvailable
								? getJoinedToSingleQueueCellContent(item)
								: getJoinedToMultipleQueuesCellContent(item)}
						</TableCell>
					)}

					{renderDesireCell && (
						<TableCell key={`${SupervisorTableColumnNames.Desire}-${item.id}`}>
							{getDesireCellContent(item)}
						</TableCell>
					)}
				</TableRow>
			);
		},
		[
			columns,
			localTableSettingsCache?.hiddenColumnIds,
			getJoinedToSingleQueueCellContent,
			getJoinedToMultipleQueuesCellContent,
			getDesireCellContent,
			isOnlyOneQueueSelectedOrAvailable,
		]
	);

	const table = useMemo(
		() => (
			<div className={mergeClasses(classes.table, classes.tableContainer)}>
				<CustomTable
					columns={
						isOnlyOneQueueSelectedOrAvailable
							? columns
							: columns.filter(
									(c) => c.columnId !== SupervisorTableColumnNames.Desire
								)
					}
					getRowTemplateCallback={getRowTemplate}
					sortable={true}
					isStickyHeader
					headerTranslationPrefix={"Supervisor"}
					columnSizing={columnSizing}
					headerSortProps={headerSortProps}
					rows={rows}
					resizable={true}
					columnsRemovable={true}
					removeColumn={removeColumn}
					classNames={{
						headerCell: classes.tableHeaderCell,
						tableClass: classes.tableClass,
					}}
				/>
			</div>
		),
		[
			isOnlyOneQueueSelectedOrAvailable,
			columns,
			getRowTemplate,
			columnSizing,
			removeColumn,
			headerSortProps,
			rows,
		]
	);

	return (
		<div
			className={mergeClasses(
				classes.leftContainer,
				classes.supervisorPanelContainer
			)}
		>
			{isMobile && <TabHeader tab={TabType.Supervisor} noBottomPadding />}
			<SupervisorPanelHeader
				additionalActions={
					isMobile
						? []
						: [
								<Button
									appearance="transparent"
									onClick={addDeletedColumn}
									title={intl.formatMessage({
										id: "Interaction.TableActions.AddDeletedColumn",
									})}
									disabled={
										columns.length === supervisedAgentsTableColumnInfo.length
									}
									className={classes.additionalActionButton}
								>
									<ArrowUndo16Regular />
								</Button>,
								<Button
									appearance="transparent"
									onClick={resetToDefaultTableSettings}
									title={intl.formatMessage({
										id: "Interaction.TableActions.ResetSettings",
									})}
									className={classes.additionalActionButton}
								>
									<ArrowCounterclockwise16Regular />
								</Button>,
								<Divider vertical />,
							]
				}
				hideSettingsButton={isMobile}
			/>
			{isMobile ? (
				<SupervisorListMobile
					supervisorTableItems={supervisorTableItems ?? []}
					supervisorShowStatusTimer={supervisorShowStatusTimer}
					getHasClientStatusIcon={getHasClientStatusIcon}
					getJoinedToMultipleQueuesCellContent={
						getJoinedToMultipleQueuesCellContent
					}
					workloadMenu={workloadMenu}
					agentTimer={agentTimer}
				/>
			) : (
				table
			)}
		</div>
	);
};
